import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

import '../styles/PatientSurveys.css';

const PatientSurveys = () => {
  const { currentUser, surveyAssignments, surveyTemplates, updateSurveyAssignment } = useAuth();
  const [localAssignments, setLocalAssignments] = useState(surveyAssignments);
  
  // Update local assignments when surveyAssignments changes
  useEffect(() => {
    setLocalAssignments(surveyAssignments);
  }, [surveyAssignments]);
  const [activeTab, setActiveTab] = useState('pending');
  const [responses, setResponses] = useState({});
  
  // Check if survey templates are loaded
  useEffect(() => {
    console.log('Survey templates loaded:', {
      count: surveyTemplates.length,
      templates: surveyTemplates.map(t => ({ id: t.id, name: t.name || t.title }))
    });
  }, [surveyTemplates]);

  // Get surveys assigned to the current patient
  const patientSurveys = localAssignments
    .filter(assignment => {
      if (!assignment) return false;
      // Ensure we're comparing the same types by converting both to strings
      const assignmentPatientId = String(assignment.patientId);
      const currentUserId = String(currentUser.uid);
      console.log('Comparing patient IDs:', { 
        assignmentId: assignment.id,
        assignmentPatientId, 
        currentUserId, 
        match: assignmentPatientId === currentUserId 
      });
      return assignmentPatientId === currentUserId;
    })
    .map(assignment => {
      // Add the template to each assignment for easier access
      const template = surveyTemplates.find(t => t.id === assignment.surveyId);
      return {
        ...assignment,
        template,
        surveyName: template?.name || template?.title || assignment.surveyTitle || 'Untitled Survey'
      };
    })
    .sort((a, b) => {
      // Sort by due date (ascending) and then by assignment date (newest first for same due date)
      const dateA = new Date(a.dueDate || 0);
      const dateB = new Date(b.dueDate || 0);
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;
      return new Date(b.assignedDate || 0) - new Date(a.assignedDate || 0);
    });
  
  const pendingSurveys = patientSurveys.filter(s => s.status === 'assigned');
  const completedSurveys = patientSurveys.filter(s => s.status === 'completed');

  const handleResponseChange = (surveyId, questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [surveyId]: {
        ...prev[surveyId],
        [questionId]: value
      }
    }));
  };

  const handleSubmitSurvey = (surveyId) => {
    const survey = patientSurveys.find(s => s.id === surveyId);
    if (!survey) return;

    // Get the template to properly map responses
    const template = surveyTemplates.find(t => t.id === survey.surveyId);
    const surveyResponses = responses[surveyId] || {};
    
    // Format responses to match question IDs
    const formattedResponses = {};
    
    if (template?.questions) {
      template.questions.forEach(question => {
        // Try to find the response for this question
        const response = surveyResponses[question.id] || 
                        surveyResponses[`q${question.id}`] ||
                        surveyResponses[question.text];
                        
        if (response !== undefined && response !== null && response !== '') {
          formattedResponses[question.id] = response;
        }
      });
    } else {
      // Fallback: use the responses as-is
      Object.assign(formattedResponses, surveyResponses);
    }

    const updatedSurvey = {
      ...survey,
      status: 'completed',
      completedDate: new Date().toISOString(),
      responses: formattedResponses,
      // Also store the raw answers for backward compatibility
      answers: formattedResponses
    };

    console.log('Submitting survey responses:', {
      surveyId,
      rawResponses: responses[surveyId],
      formattedResponses,
      updatedSurvey
    });

    updateSurveyAssignment(updatedSurvey);
    setResponses(prev => {
      const newResponses = { ...prev };
      delete newResponses[surveyId];
      return newResponses;
    });
  };

  const renderQuestionInput = (surveyId, question) => {
    switch (question.type) {
      case 'rating':
        return (
          <div className="rating-input">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`star ${responses[surveyId]?.[question.id] >= star ? 'active' : ''}`}
                onClick={() => handleResponseChange(surveyId, question.id, star)}
              >
                ★
              </button>
            ))}
          </div>
        );
      case 'multiple-choice':
      case 'checkbox':
        return (
          <div className="options-container">
            {question.options.map((option, index) => (
              <label key={index} className="option-label">
                <input
                  type={question.type === 'checkbox' ? 'checkbox' : 'radio'}
                  name={`${surveyId}-${question.id}`}
                  value={option}
                  onChange={(e) => {
                    const value = question.type === 'checkbox'
                      ? {
                          ...(responses[surveyId]?.[question.id] || {}),
                          [option]: e.target.checked
                        }
                      : option;
                    handleResponseChange(surveyId, question.id, value);
                  }}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'yes-no':
        return (
          <div className="yes-no-container">
            {['Yes', 'No'].map((option) => (
              <label key={option} className="option-label">
                <input
                  type="radio"
                  name={`${surveyId}-${question.id}`}
                  value={option}
                  checked={responses[surveyId]?.[question.id] === option}
                  onChange={() => handleResponseChange(surveyId, question.id, option)}
                />
                {option}
              </label>
            ))}
          </div>
        );
      case 'textarea':
        return (
          <textarea
            className="form-control"
            value={responses[surveyId]?.[question.id] || ''}
            onChange={(e) => handleResponseChange(surveyId, question.id, e.target.value)}
            rows={3}
          />
        );
      default:
        return (
          <input
            type="text"
            className="form-control"
            value={responses[surveyId]?.[question.id] || ''}
            onChange={(e) => handleResponseChange(surveyId, question.id, e.target.value)}
          />
        );
    }
  };

  const getSurveyDisplayName = (survey, index) => {
    if (survey.surveyTitle) return survey.surveyTitle;
    // Add a number suffix if there are multiple instances of the same survey
    const similarSurveys = patientSurveys.filter(s => s.surveyId === survey.surveyId);
    if (similarSurveys.length > 1) {
      const surveyIndex = similarSurveys.findIndex(s => s.id === survey.id) + 1;
      return `${survey.surveyName} #${surveyIndex}`;
    }
    return survey.surveyName;
  };

  const renderSurveyCard = (survey) => {
    const template = surveyTemplates.find(t => t.id === survey.surveyId);
    if (!template) return null;

    // Check if survey is overdue
    const isOverdue = new Date(survey.dueDate) < new Date() && survey.status !== 'completed';
    const cardClass = `survey-card ${survey.status} ${isOverdue ? 'overdue' : ''}`;

    return (
      <div key={survey.id} className={cardClass}>
        <div className="survey-header">
          <div>
            <span className="survey-type">{template.name}</span>
            <h3>{getSurveyDisplayName(survey)}</h3>
          </div>
          <span className={`status-badge ${survey.status} ${isOverdue ? 'overdue' : ''}`}>
            {isOverdue ? 'Overdue' : (survey.status === 'completed' ? 'Completed' : 'Pending')}
          </span>
        </div>
        <p className="survey-description">{survey.surveyDescription}</p>
        <div className="survey-meta">
          <div className="meta-item">
            <span className="meta-label">Assigned</span>
            <span className="meta-value">{new Date(survey.assignedDate).toLocaleDateString()}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Due Date</span>
            <span className="meta-value">{new Date(survey.dueDate).toLocaleDateString()}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Priority</span>
            <span className={`priority-badge priority-${survey.priority}`}>
              {survey.priority}
            </span>
          </div>
        </div>

        {survey.status === 'assigned' && (
          <div className="survey-questions">
            {template.questions.map((question) => (
              <div key={question.id} className="question-container">
                <label className="question-label">
                  {question.question}
                  {question.required && <span className="required">*</span>}
                </label>
                {renderQuestionInput(survey.id, question)}
              </div>
            ))}
            <button
              className="btn btn-primary"
              onClick={() => handleSubmitSurvey(survey.id)}
            >
              Submit Survey
            </button>
          </div>
        )}

        {survey.status === 'completed' && (
          <div className="completed-message">
            <p>✓ Completed on {new Date(survey.completedDate).toLocaleDateString()}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="patient-surveys">
      <div className="surveys-header">
        <h2>My Surveys</h2>
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending ({pendingSurveys.length})
          </button>
          <button
            className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => setActiveTab('completed')}
          >
            Completed ({completedSurveys.length})
          </button>
        </div>
      </div>

      <div className="surveys-container">
        {activeTab === 'pending' ? (
          pendingSurveys.length > 0 ? (
            pendingSurveys.map(renderSurveyCard)
          ) : (
            <div className="no-surveys">
              <p>You don't have any pending surveys at the moment.</p>
            </div>
          )
        ) : completedSurveys.length > 0 ? (
          completedSurveys.map(renderSurveyCard)
        ) : (
          <div className="no-surveys">
            <p>You haven't completed any surveys yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientSurveys;
